/**
 * Entrance Effects Module
 * Handles one-time reveal animations when elements enter the viewport
 * 
 * Classes:
 * - .e-in-from-left  : Slides in from left when entering viewport
 * - .e-in-from-right : Slides in from right when entering viewport
 * - .e-fade-in-up    : Fades in while sliding up into position
 * 
 * Uses CSS custom properties to combine with parallax effects:
 * - --entrance-x : Horizontal entrance offset
 * - --entrance-y : Vertical entrance offset
 */

'use strict';

// =============================================================================
// CONFIGURATION - Edit these values to adjust animation behavior
// =============================================================================

const CONFIG = {
	// How far elements start from their final position (pixels)
	offsetX: 40,
	offsetY: 30,
	
	// Animation duration (CSS transition)
	duration: '0.8s',
	
	// Easing function
	easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
	
	// Threshold: how much of element must be visible to trigger (0-1)
	threshold: 0.1,
};

// =============================================================================
// ENTRANCE ANIMATIONS
// =============================================================================

const ENTRANCE_CLASSES = [
	'.e-in-from-left',
	'.e-in-from-right',
	'.e-fade-in-up'
];

const entranceElements = document.querySelectorAll(ENTRANCE_CLASSES.join(', '));

if (entranceElements.length > 0) {
	/**
	 * Get starting offsets for entrance animations
	 * Returns { x, y } offset values
	 */
	const getStartOffsets = (element) => {
		if (element.classList.contains('e-in-from-left')) {
			return { x: -CONFIG.offsetX, y: 0 };
		}
		if (element.classList.contains('e-in-from-right')) {
			return { x: CONFIG.offsetX, y: 0 };
		}
		if (element.classList.contains('e-fade-in-up')) {
			return { x: 0, y: CONFIG.offsetY };
		}
		return { x: 0, y: 0 };
	};
	
	/**
	 * Apply combined transform using CSS custom properties
	 */
	const applyTransform = (element) => {
		const entranceX = parseFloat(element.style.getPropertyValue('--entrance-x')) || 0;
		const entranceY = parseFloat(element.style.getPropertyValue('--entrance-y')) || 0;
		const parallaxX = parseFloat(element.style.getPropertyValue('--parallax-x')) || 0;
		const parallaxY = parseFloat(element.style.getPropertyValue('--parallax-y')) || 0;
		
		element.style.transform = `translate(${entranceX + parallaxX}px, ${entranceY + parallaxY}px)`;
	};
	
	// Set initial state for all entrance elements
	entranceElements.forEach((element) => {
		const offsets = getStartOffsets(element);
		element.style.setProperty('--entrance-x', offsets.x);
		element.style.setProperty('--entrance-y', offsets.y);
		element.style.opacity = '0';
		element.style.transition = `transform ${CONFIG.duration} ${CONFIG.easing}, opacity ${CONFIG.duration} ${CONFIG.easing}`;
		applyTransform(element);
	});
	
	/**
	 * Animate element into view
	 */
	const animateIn = (element) => {
		element.style.setProperty('--entrance-x', 0);
		element.style.setProperty('--entrance-y', 0);
		element.style.opacity = '1';
		applyTransform(element);
		
		// Remove transition after animation completes so parallax can update instantly
		const transitionDuration = parseFloat(CONFIG.duration) * 1000;
		setTimeout(() => {
			element.style.transition = 'opacity 0.3s ease'; // Keep subtle opacity transition, remove transform transition
			element.dataset.entranceComplete = 'true';
		}, transitionDuration);
	};
	
	// Use IntersectionObserver for efficient viewport detection
	const observer = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				animateIn(entry.target);
				// Stop observing once animated
				observer.unobserve(entry.target);
			}
		});
	}, {
		threshold: CONFIG.threshold,
		rootMargin: '0px'
	});
	
	// Observe all entrance elements
	entranceElements.forEach((element) => {
		observer.observe(element);
	});
}
